/** LEP-00004 — Enterprise Acceptance Platform framework (mirrors pkg/enterpriseacceptance) */

export const LEP_EA_ID = 'LEP-00004';
export const INTERNAL_TENANT = 'Nakabh';

export interface CommandCenterScore {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface TenantOpRow {
  process: string;
  status: string;
  lastRun: string;
}

export interface MarketplaceValidationRow {
  area: string;
  status: string;
  score: number;
  evidence: string;
}

export interface FeatureAcceptanceRow {
  feature: string;
  owner: string;
  status: string;
  severity: string;
  signedOff: boolean;
}

export interface ModuleAcceptanceRow {
  category: string;
  module: string;
  status: string;
  score: number;
}

export interface AIAlphaValidationRow {
  check: string;
  score: number;
  status: string;
  detail: string;
}

export interface UATSessionRow {
  department: string;
  scenarios: number;
  passed: number;
  status: string;
}

export interface GoLiveReadinessRow {
  artifact: string;
  status: string;
  progress: number;
  owner: string;
}

export const COMMAND_CENTER_METRICS = [
  'Overall Alpha Readiness', 'Business Readiness', 'Module Readiness', 'AI Readiness',
  'Marketplace Readiness', 'Security Readiness', 'Performance Readiness', 'Go-Live Confidence',
];

export const MARKETPLACE_VALIDATION_AREAS = [
  'Amazon SP-API', 'Catalog', 'Listings', 'Inventory', 'Advertising',
  'Orders', 'Returns', 'Settlements', 'Reports', 'Webhooks',
];

export const FEATURE_ACCEPTANCE_FIELDS = [
  'Acceptance Criteria', 'Evidence', 'Screenshots', 'Videos', 'Owner',
  'Status', 'Severity', 'Retest Results', 'Sign-off',
];

export const MODULE_ACCEPTANCE_CATEGORIES = [
  'Every UXP', 'Every LEP', 'Performance', 'Usability', 'Business Value',
  'Documentation', 'AI Quality', 'Accessibility',
];

export const AI_ALPHA_VALIDATION = [
  'Content Generation', 'Image Generation', 'Video Generation', 'A+ Content', 'Advertising',
  'Forecasting', 'Automation', 'Prompt Quality', 'DNA Compliance', 'Marketplace Compliance',
];

export const UAT_CAPABILITIES = [
  'Department-wise Testing', 'Scenario Testing', 'Issue Tracking',
  'Improvement Requests', 'Acceptance Sign-offs',
];

export const FEEDBACK_TYPES = [
  'Bugs', 'Enhancements', 'Pain Points', 'Missing Features',
  'Workflow Improvements', 'Performance Issues',
];

export const BUSINESS_KPIS = [
  'Revenue', 'Profitability', 'Inventory Accuracy',
  'Advertising Results', 'Operational Efficiency', 'Financial Accuracy',
];

export const DOCUMENTATION_AREAS = [
  'Knowledge Base', 'Training', 'Tutorials', 'API Documentation', 'Runbooks', 'Release Notes',
];

export const GO_LIVE_ARTIFACTS = [
  'Go-Live Checklist', 'Risk Register', 'Owner Matrix', 'Evidence Repository', 'Release Recommendation',
];

export const LESSONS_LEARNED = [
  'Root Causes', 'Corrective Actions', 'Preventive Actions', 'Knowledge Updates', 'Automation Opportunities',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Alpha Command Center', 'Internal Tenant', 'Marketplace Validation', 'Feature Acceptance',
  'Module Acceptance', 'AI Validation', 'User Acceptance', 'Go-Live Readiness',
];

export const LIVE_ROUTES: Record<string, string> = {
  alpha: '/alpha',
  enterpriseQuality: '/platform/enterprise-quality',
  enterpriseDevOps: '/platform/enterprise-devops',
};

export const DEMO_SCORES: CommandCenterScore[] = [
  { label: 'Overall Alpha Readiness', value: '88%', change: '+6%', positive: true },
  { label: 'Business Readiness', value: '92%', change: '+8%', positive: true },
  { label: 'Module Readiness', value: '86%', change: '+12%', positive: true },
  { label: 'AI Readiness', value: '84%', change: '+10%', positive: true },
  { label: 'Marketplace Readiness', value: '94%', change: '+4%', positive: true },
  { label: 'Security Readiness', value: '98%', change: '+1%', positive: true },
  { label: 'Performance Readiness', value: '92%', change: '+5%', positive: true },
  { label: 'Go-Live Confidence', value: 'High', change: '2 blockers', positive: true },
];

export const DEMO_TENANT_OPS: TenantOpRow[] = [
  { process: 'Daily Catalog Sync', status: 'completed', lastRun: 'Today 06:00' },
  { process: 'Order Processing', status: 'completed', lastRun: 'Today 14:32' },
  { process: 'Inventory Reconciliation', status: 'completed', lastRun: 'Today 08:15' },
  { process: 'Advertising Optimization', status: 'in_progress', lastRun: 'Today 12:00' },
  { process: 'Financial Settlement', status: 'completed', lastRun: 'Yesterday' },
  { process: 'AI Listing Optimization', status: 'completed', lastRun: 'Today 10:45' },
];

export const DEMO_MARKETPLACE: MarketplaceValidationRow[] = [
  { area: 'Amazon SP-API', status: 'validated', score: 98, evidence: 'OAuth + LWA connected' },
  { area: 'Catalog', status: 'validated', score: 96, evidence: '8,420 SKUs synced' },
  { area: 'Listings', status: 'validated', score: 94, evidence: '842 AI listings' },
  { area: 'Inventory', status: 'validated', score: 97, evidence: '99.2% accuracy' },
  { area: 'Advertising', status: 'in_progress', score: 82, evidence: 'Campaign sync pending' },
  { area: 'Orders', status: 'validated', score: 99, evidence: '1,248 orders processed' },
  { area: 'Returns', status: 'validated', score: 95, evidence: '42 returns handled' },
  { area: 'Settlements', status: 'validated', score: 96, evidence: '₹14.6L reconciled' },
];

export const DEMO_FEATURES: FeatureAcceptanceRow[] = [
  { feature: 'Amazon Connect OAuth Flow', owner: 'Platform Team', status: 'signed_off', severity: 'critical', signedOff: true },
  { feature: 'AI Listing Optimizer', owner: 'AI Team', status: 'signed_off', severity: 'critical', signedOff: true },
  { feature: 'Catalog Sync Pipeline', owner: 'Commerce Team', status: 'passed', severity: 'critical', signedOff: false },
  { feature: 'Workflow Orchestration', owner: 'Platform Team', status: 'in_progress', severity: 'high', signedOff: false },
  { feature: 'Billing & Subscriptions', owner: 'Finance Team', status: 'passed', severity: 'high', signedOff: false },
];

export const DEMO_MODULES: ModuleAcceptanceRow[] = [
  { category: 'Every UXP', module: 'UXP-00001 through UXP-00018', status: 'certified', score: 92 },
  { category: 'Every LEP', module: 'LEP-00001 through LEP-00004', status: 'in_progress', score: 86 },
  { category: 'Performance', module: 'Platform Performance', status: 'certified', score: 94 },
  { category: 'AI Quality', module: 'AI Services', status: 'certified', score: 88 },
  { category: 'Accessibility', module: 'WCAG 2.2 AA', status: 'certified', score: 96 },
];

export const DEMO_AI_VALIDATION: AIAlphaValidationRow[] = [
  { check: 'Content Generation', score: 94, status: 'passed', detail: '842 listings optimized' },
  { check: 'Image Generation', score: 91, status: 'passed', detail: '420 creatives generated' },
  { check: 'A+ Content', score: 88, status: 'passed', detail: '124 A+ pages created' },
  { check: 'DNA Compliance', score: 96, status: 'passed', detail: 'Brand DNA enforced' },
  { check: 'Marketplace Compliance', score: 93, status: 'passed', detail: 'Amazon compliance validated' },
];

export const DEMO_UAT: UATSessionRow[] = [
  { department: 'Operations', scenarios: 24, passed: 22, status: 'in_progress' },
  { department: 'Finance', scenarios: 12, passed: 12, status: 'signed_off' },
  { department: 'Marketing', scenarios: 18, passed: 16, status: 'in_progress' },
  { department: 'Catalog', scenarios: 20, passed: 20, status: 'signed_off' },
];

export const DEMO_GO_LIVE: GoLiveReadinessRow[] = [
  { artifact: 'Go-Live Checklist', status: 'in_progress', progress: 88, owner: 'Product Owner' },
  { artifact: 'Risk Register', status: 'current', progress: 100, owner: 'Enterprise Architect' },
  { artifact: 'Owner Matrix', status: 'complete', progress: 100, owner: 'Program Manager' },
  { artifact: 'Evidence Repository', status: 'in_progress', progress: 76, owner: 'QA Lead' },
  { artifact: 'Release Recommendation', status: 'draft', progress: 60, owner: 'Executive Sponsor' },
];

export function buildAcceptanceDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    marketplace_areas: MARKETPLACE_VALIDATION_AREAS.length,
    module_categories: MODULE_ACCEPTANCE_CATEGORIES.length,
    ai_validation_checks: AI_ALPHA_VALIDATION.length,
    go_live_artifacts: GO_LIVE_ARTIFACTS.length,
  };
}
